import React, { useEffect, useRef, useState } from "react";
import logo from "../assets/logo.png";

const Section = ({ id, number, title, children, delay = 0 }) => {
    const [visible, setVisible] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.1 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, []);

    return (
        <section
            id={id}
            ref={ref}
            className={`scroll-mt-24 transition-all duration-700 ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
            style={{ transitionDelay: `${delay}ms` }}
        >
            <div className="flex items-center gap-4 mb-5">
                <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400 font-black text-sm shrink-0">
                    {number}
                </span>
                <h2 className="text-2xl font-bold text-white">{title}</h2>
            </div>
            <div className="pl-14">{children}</div>
        </section>
    );
};

const TocItem = ({ href, number, title, active }) => (
    <a
        href={href}
        className={`group flex items-center gap-3 py-2 text-sm transition-all duration-200 ${active ? "text-amber-400 font-semibold" : "text-slate-500 hover:text-slate-300"}`}
    >
        <span className={`w-6 h-6 flex items-center justify-center rounded-md text-[10px] font-bold transition-colors ${active ? "bg-amber-500 text-slate-950" : "bg-slate-800 text-slate-500 group-hover:bg-slate-700"}`}>
            {number}
        </span>
        <span className="line-clamp-1">{title}</span>
    </a>
);

const InfoCard = ({ icon, title, items }) => (
    <div className="p-5 rounded-xl bg-slate-950/50 border border-slate-800/50">
        <div className="flex items-center gap-2 mb-4">
            <i className={`bi ${icon} text-amber-500`} aria-hidden="true" />
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">{title}</h3>
        </div>
        <ul className="space-y-3">
            {items.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-300">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500/60 mt-1.5 shrink-0" />
                    {item}
                </li>
            ))}
        </ul>
    </div>
);

export default function Terms() {
    const [activeSection, setActiveSection] = useState("intro");
    const [progress, setProgress] = useState(0);

    const sections = [
        { id: "intro", number: "1", title: "Acceptance of Terms" },
        { id: "services", number: "2", title: "Provision of Services" },
        { id: "accounts", number: "3", title: "User Accounts & Security" },
        { id: "payments", number: "4", title: "Payments, Fees & Billing" },
        { id: "liability", number: "5", title: "Limitation of Liability" },
        { id: "termination", number: "6", title: "Contract Termination" },
        { id: "contact", number: "7", title: "Contact Us" },
    ];

    useEffect(() => {
        const onScroll = () => {
            const docHeight = document.documentElement.scrollHeight - window.innerHeight;
            setProgress(docHeight > 0 ? (window.scrollY / docHeight) * 100 : 0);

            for (let i = sections.length - 1; i >= 0; i--) {
                const el = document.getElementById(sections[i].id);
                if (el) {
                    const rect = el.getBoundingClientRect();
                    if (rect.top <= 150) {
                        setActiveSection(sections[i].id);
                        break;
                    }
                }
            }
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <div className="bg-slate-950 min-h-screen text-slate-300 relative overflow-hidden">
            {/* Ambient background */}
            <div className="fixed inset-0 pointer-events-none">
                <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-amber-500/5 blur-[150px] rounded-full" />
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-600/5 blur-[120px] rounded-full" />
            </div>

            {/* Scroll Progress */}
            <div className="fixed top-0 left-0 w-full h-[3px] z-50 bg-transparent">
                <div
                    className="h-full bg-gradient-to-r from-amber-400 to-amber-600 transition-all duration-150 ease-out"
                    style={{ width: `${progress}%` }}
                />
            </div>

            <div className="max-w-7xl mx-auto px-6 py-24 relative z-10">
                <div className="grid lg:grid-cols-12 gap-12">

                    {/* Sidebar Navigation */}
                    <aside className="hidden lg:block lg:col-span-3">
                        <div className="sticky top-28 space-y-8">
                            <div>
                                <span className="text-xs font-bold uppercase tracking-widest text-amber-500 mb-4 block">
                                    Table of Contents
                                </span>
                                <nav className="space-y-1 border-l-2 border-slate-800 pl-4">
                                    {sections.map((s) => (
                                        <TocItem
                                            key={s.id}
                                            href={`#${s.id}`}
                                            number={s.number}
                                            title={s.title}
                                            active={activeSection === s.id}
                                        />
                                    ))}
                                </nav>
                            </div>

                            <div className="p-5 rounded-2xl bg-slate-900/80 border border-slate-800">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-10 h-10 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400">
                                        <i className="bi bi-shield-check" aria-hidden="true" />
                                    </div>
                                    <div>
                                        <p className="text-white font-bold text-sm">Questions?</p>
                                        <p className="text-xs text-slate-500">We're here to help</p>
                                    </div>
                                </div>
                                <a
                                    href="mailto:compliance@delightgalaxy.com"
                                    className="block w-full text-center py-2.5 rounded-xl bg-amber-500 text-slate-950 font-bold text-sm hover:bg-amber-400 transition-colors"
                                >
                                    Email Compliance
                                </a>
                            </div>
                        </div>
                    </aside>

                    {/* Main Content */}
                    <main className="lg:col-span-9">
                        <div className="bg-slate-900/60 backdrop-blur-sm rounded-3xl border border-slate-800 shadow-2xl overflow-hidden">

                            {/* Header */}
                            <div className="relative p-8 md:p-12 pb-8 border-b border-slate-800 bg-gradient-to-br from-slate-900 to-slate-800/50">
                                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20 text-xs font-semibold mb-6">
                                    <i className="bi bi-shield-check" aria-hidden="true" />
                                    Legal Terms
                                </div>

                                <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-white font-serif leading-tight">
                                    Terms of <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Service</span>
                                </h1>

                                <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-slate-500">
                                    <span className="flex items-center gap-2">
                                        <i className="bi bi-calendar3" aria-hidden="true" />
                                        Last Updated: July 30, 2026
                                    </span>
                                </div>
                            </div>

                            {/* Body content */}
                            <div className="p-8 md:p-12 space-y-12 leading-relaxed text-slate-400">

                                <Section id="intro" number="1" title="Acceptance of Terms" delay={100}>
                                    <p className="mb-4">
                                        Welcome to Milestone and Delight Galaxy Limited ("Company", "we", "us", or "our"). 
                                        By accessing or using our websites, software products, mobile applications, custom integrations 
                                        (including but not limited to EFRIS and SAP Business One Solutions), or IT infrastructure services, 
                                        you agree to be bound by these Terms of Service.
                                    </p>
                                    <p>
                                        If you do not agree to all of the terms and conditions outlined in this agreement, 
                                        you must not access our software portals or utilize our technical consultancy services.
                                    </p>
                                </Section>

                                <Section id="services" number="2" title="Provision of Services" delay={100}>
                                    <p className="mb-6">
                                        Milestone and Delight Galaxy Limited designs, develops, installs, and hosts custom software integrations, 
                                        managed database systems, and compliance frameworks.
                                    </p>
                                    <div className="grid md:grid-cols-2 gap-6">
                                        <InfoCard
                                            icon="bi-code-square"
                                            title="Custom Integrations"
                                            items={[
                                                "Uganda Revenue Authority EFRIS API connections.",
                                                "SAP Business One custom databases and dashboards.",
                                                "Local retail POS and e-commerce bridge nodes."
                                            ]}
                                        />
                                        <InfoCard
                                            icon="bi-cloud-check"
                                            title="Infrastructure & Hosting"
                                            items={[
                                                "Cloud backups and infrastructure optimization.",
                                                "Secure web application server provisioning.",
                                                "IT consulting and security network deployments."
                                            ]}
                                        />
                                    </div>
                                </Section>

                                <Section id="accounts" number="3" title="User Accounts & Security" delay={100}>
                                    <p className="mb-4">
                                        Certain features or customized system environments require registered portal profiles or 
                                        administrator privileges. You are entirely responsible for:
                                    </p>
                                    <ul className="list-disc pl-5 space-y-2 mb-4">
                                        <li>Maintaining the confidentiality of system credentials, API tokens, and client passwords.</li>
                                        <li>Restricting access to client systems to authorized personnel only.</li>
                                        <li>Notifying our technical support team immediately of any unauthorized access, breach of credential integrity, or suspected leak.</li>
                                    </ul>
                                    <p>
                                        We will not be held liable for losses or business disruptions arising from compromised account credentials 
                                        or client-managed database servers.
                                    </p>
                                </Section>

                                <Section id="payments" number="4" title="Payments, Fees & Billing" delay={100}>
                                    <p className="mb-4">
                                        Access to proprietary software licenses, custom integrations, cloud server hosting, and support contracts 
                                        is subject to the payment of fees as specified in project agreements or service level agreements (SLAs).
                                    </p>
                                    <p className="mb-4">
                                        Invoices are generated on recurring intervals (e.g., monthly cloud billing, annual support plans) or 
                                        milestone completions. Accounts that remain unpaid past the designated grace period may experience 
                                        temporary system suspension, API connection halts, or support termination.
                                    </p>
                                </Section>

                                <Section id="liability" number="5" title="Limitation of Liability" delay={100}>
                                    <p className="mb-4">
                                        Milestone and Delight Galaxy Limited provides custom software, tax gateways, and hosting networks 
                                        "as is" and "as available". We make no warranties, express or implied, regarding uptime, software accuracy, 
                                        or fit for purpose beyond explicit SLA deliverables.
                                    </p>
                                    <p>
                                        Under no circumstances shall the Company, its directors, or its developers be liable for indirect, 
                                        consequential, or special damages, including lost revenue, system down-time, or audit fines, arising out of 
                                        the use or inability to use our integrations.
                                    </p>
                                </Section>

                                <Section id="termination" number="6" title="Contract Termination" delay={100}>
                                    <p className="mb-4">
                                        Either party may terminate a service agreement by providing written notice in accordance with the 
                                        specific contract timeline (usually 30 days prior notice).
                                    </p>
                                    <p>
                                        Upon contract closure or deletion request, all configurations, backups, and databases will be either 
                                        handed over to the client or securely purged in compliance with our dedicated Data Deletion Policy.
                                    </p>
                                </Section>

                                <Section id="contact" number="7" title="Contact Us" delay={100}>
                                    <p className="mb-6">
                                        If you have questions, disputes, or clarifications regarding these Terms of Service, 
                                        please reach out to our team:
                                    </p>

                                    <div className="flex flex-col sm:flex-row items-start gap-6 p-6 rounded-2xl bg-slate-950 border border-slate-800">
                                        <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-slate-900 border border-slate-800 shrink-0 overflow-hidden">
                                            <img
                                                src={logo}
                                                alt="Delight Galaxy"
                                                className="w-10 h-10 object-contain"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.parentElement.innerHTML = '<span class="text-white font-black text-xl">D</span>';
                                                }}
                                            />
                                        </div>

                                        <div className="space-y-3 text-sm">
                                            <p className="text-white font-bold text-lg">Delight Galaxy Limited</p>

                                            <a href="mailto:smuwanguzi2@gmail.com" className="flex items-center gap-2 text-slate-400 hover:text-amber-400 transition-colors">
                                                <i className="bi bi-envelope text-amber-500" aria-hidden="true" />
                                                smuwanguzi2@gmail.com
                                            </a>

                                            <a href="https://milestoneanddelight.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-slate-400 hover:text-amber-400 transition-colors">
                                                <i className="bi bi-globe text-amber-500" aria-hidden="true" />
                                                milestoneanddelight.com
                                            </a>

                                            <p className="flex items-center gap-2 text-slate-400">
                                                <i className="bi bi-geo-alt text-amber-500" aria-hidden="true" />
                                                Kampala, Uganda
                                            </p>
                                        </div>
                                    </div>
                                </Section>

                            </div>

                            {/* Footer of card */}
                            <div className="px-8 md:px-12 py-6 border-t border-slate-800 bg-slate-950/30 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-slate-600">
                                <p>© {new Date().getFullYear()} Delight Galaxy Limited. All rights reserved.</p>
                                <div className="flex items-center gap-2">
                                    <i className="bi bi-shield-check text-emerald-500/80" aria-hidden="true" />
                                    <span>Terms & Licensing Standard Agreement</span>
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>
        </div>
    );
}
